import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'

import { httpOk, GET, PUT } from 'helpers/api'
import { exportEmployeeReport } from './employeeReportExcel'
import useTableDragScroll from 'pages/parts/useTableDragScroll'
import { renderSortIndicator, patchSortFilterOnColumnClick } from 'helpers/tableSortIndicator'
import { copyText, formatMoney, returnSign } from 'helpers/helpers'

function Index({ filterData, isActive }) {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [loadedFilter, setLoadedFilter] = useState(null)
	const [selectedItem, setSelectedItem] = useState({})
	const [modalDetail, setModalDetail] = useState(false)
	const [products, setProducts] = useState([])
	const [excludedProducts, setExcludedProducts] = useState([])
	const [selectedExcluded, setSelectedExcluded] = useState([])
	const [modalExcluded, setModalExcluded] = useState(false)
	const [motivationScope, setMotivationScope] = useState(false)
	// Сортировка на клиенте: строк — по числу официантов, запрос заново не нужен
	const [sortState, setSortState] = useState({ order_by: null, sort: 'asc' })
	const tableDragScroll = useTableDragScroll()

	async function searchWithFilter(scope = motivationScope) {
		var filterUrl = "/report-waiters"
		var urlParams = ""

		if (filterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterData.pos_id
		if (filterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterData.start_date
		if (filterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterData.end_date
		if (scope)
			urlParams += returnSign(urlParams) + 'motivation_scope=1'

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setLoadedFilter(JSON.stringify(filterData))
		}
	}

	// Исключённые позиции (таймер зала и т.п.) хранятся на точку, поэтому берём pos_id из фильтра
	async function getExcludedProducts() {
		const urlParams = filterData.pos_id ? '?pos_id=' + filterData.pos_id : ''
		const response = await GET('/report-waiters/excluded-products' + urlParams)
		if (httpOk(response)) setExcludedProducts(response.data)
	}

	async function openModalExcluded() {
		if (products.length === 0) {
			const response = await GET('/helper/all-products', {}, { loader: true })
			if (httpOk(response)) setProducts(response.data)
		}
		setSelectedExcluded(excludedProducts)
		setModalExcluded(true)
	}

	async function saveExcluded() {
		const response = await PUT('/report-waiters/excluded-products', {
			pos_id: filterData.pos_id,
			product_ids: selectedExcluded.map(item => item.id),
		}, { loader: true })
		if (httpOk(response)) {
			setExcludedProducts(selectedExcluded)
			setModalExcluded(false)
			searchWithFilter()
		}
	}

	function toggleMotivationScope(value) {
		setMotivationScope(value)
		searchWithFilter(value)
	}

	function toggleModalDetail(item) {
		setSelectedItem(item)
		setModalDetail(true)
	}

	function share(amount) {
		if (!data?.overall?.overall_sales) return '0%'
		return formatMoney(amount * 100 / data.overall.overall_sales, 1) + '%'
	}

	// Ставка программы мотивации: процент («6.5%»), сумма за штуку / за чек / за период
	function formatRate(rate) {
		const value = Number(rate.value) || 0
		if (rate.type === 'percent') return (Math.round(value * 100) / 100) + '%'
		return t('motivation_rate_' + rate.type, { value: formatMoney(value) })
	}

	// У официанта может быть несколько программ — ставки через точку, названия программ в подсказке
	function formatRates(rates) {
		return (rates || []).map(rate => formatRate(rate)).join(' · ')
	}

	function ratesTitle(rates) {
		return (rates || []).map(rate => (rate.name || '') + ': ' + formatRate(rate)).join('\n')
	}

	// Значение колонки для сортировки: расчётные колонки (средний чек, в день, ставка) считаются тут же
	const sortValues = {
		first_name: item => item.first_name || '',
		working_days: item => Number(item.working_days) || 0,
		count_cheque: item => Number(item.count_cheque) || 0,
		people_number: item => Number(item.people_number) || 0,
		count_products: item => Number(item.count_products) || 0,
		average_cheque: item => item.count_cheque > 0 ? item.sales / item.count_cheque : 0,
		sales_per_day: item => item.working_days > 0 ? item.sales / item.working_days : 0,
		total_service_amount: item => Number(item.total_service_amount) || 0,
		sales: item => Number(item.sales) || 0,
		gross: item => Number(item.gross) || 0,
		// Несколько программ — по наибольшей ставке
		motivation_rates: item => Math.max(0, ...(item.motivation_rates || []).map(rate => Number(rate.value) || 0)),
		motivation_bonus: item => Number(item.motivation_bonus) || 0,
	}

	const sortedRows = useMemo(() => {
		const rows = [...(data?.data || [])]
		const getValue = sortValues[sortState.order_by]
		if (!getValue) return rows
		const mul = sortState.sort === 'desc' ? -1 : 1
		rows.sort((a, b) => {
			const va = getValue(a)
			const vb = getValue(b)
			if (typeof va === 'string') return va.localeCompare(vb) * mul
			return (va - vb) * mul
		})
		return rows
	}, [data, sortState]) // eslint-disable-line react-hooks/exhaustive-deps

	function sortTable(columnKey) {
		setSortState(patchSortFilterOnColumnClick(sortState, columnKey))
	}

	// Заголовок с сортировкой — как в «Валовом доходе»: подпись + стрелка, клик меняет направление.
	// Подпись переносится по словам: в одну строку длинные заголовки раздували колонки с числами
	function sortableTh(columnKey, label, style = { 'width': '1%' }, isText = false) {
		return (
			<th style={style} className={(isText ? '' : 'text-end ') + 'th-sortable user-select-none align-middle'}
				onClick={() => sortTable(columnKey)} title={t('sort_by')}>
				<div className={'d-flex align-items-center gap-1 ' + (isText ? 'justify-content-between' : 'justify-content-end')}>
					<span>{label}</span>
					{renderSortIndicator(columnKey, sortState)}
				</div>
			</th>
		)
	}

	function exportToExcel() {
		var rows = sortedRows
		var o = data?.overall || {}
		var summary = [['#', t('waiter'), t('working_days'), t('cheques'), t('guests'), t('products'), t('average_cheque'), t('sales_per_day'), t('service'), t('sales_amount'), t('share_of_sales') + ', %', t('gross_income'), t('motivation_rate'), t('motivation_accrued')]]
		rows.forEach((item, index) => summary.push([
			index + 1, item.first_name, Number(item.working_days), Number(item.count_cheque), Number(item.people_number), Number(item.count_products),
			Math.round(item.count_cheque > 0 ? item.sales / item.count_cheque : 0),
			Math.round(item.working_days > 0 ? item.sales / item.working_days : 0),
			Number(item.total_service_amount), Number(item.sales),
			o.overall_sales ? Math.round(item.sales * 1000 / o.overall_sales) / 10 : 0,
			Number(item.gross), formatRates(item.motivation_rates), Number(item.motivation_bonus || 0),
		]))
		summary.push(['', t('total'), '', Number(o.overall_cheques), Number(o.overall_people), Number(o.overall_products),
			Math.round(o.overall_cheques > 0 ? o.overall_sales / o.overall_cheques : 0), '',
			Number(o.overall_service_amount), Number(o.overall_sales), 100, Number(o.overall_gross), '', Number(o.overall_motivation_bonus || 0)])
		exportEmployeeReport(t, t('waiter_report'), summary, rows, 'total_amount', 'total_income')
	}

	useEffect(() => {
		// Вкладка не открыта или данные под этот фильтр уже загружены — запрос не шлём
		if (!isActive || !filterData) return
		if (loadedFilter === JSON.stringify(filterData)) return
		searchWithFilter()
		getExcludedProducts()
	}, [filterData, isActive]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
						<h5 className="mb-0">{t('waiter_report')}</h5>
						<div className="d-flex align-items-center flex-wrap gap-3">
							<div className="form-check form-switch mb-0" title={t('motivation_scope_hint')}>
								<input className="form-check-input" type="checkbox" id="motivation_scope"
									checked={motivationScope}
									onChange={(e) => toggleMotivationScope(e.target.checked)} />
								<label className="form-check-label" htmlFor="motivation_scope">{t('motivation_scope')}</label>
							</div>
							<button className="btn btn-outline-primary btn-sm" title="Excel" onClick={() => exportToExcel()} disabled={!data?.data?.length}>
								<i className="uil uil-file-download-alt"></i>
							</button>
							<button className="btn btn-outline-primary btn-sm" onClick={() => openModalExcluded()}>
								<i className="uil-filter me-1"></i>
								{t('excluded_products')}
								{excludedProducts.length > 0 &&
									<span className="badge bg-primary ms-2">{excludedProducts.length}</span>
								}
							</button>
						</div>
					</div>

					{excludedProducts.length > 0 &&
						<div className="mb-3 small text-muted">
							<b>{t('excluded_products')}: </b>
							{excludedProducts.map(item => item.name).join(', ')}
						</div>
					}

					{/* Скролл внутри блока: протяжка мышью по горизонтали, шапка, «#» с официантом и итог липнут */}
					<div className="table-responsive" style={{ 'maxHeight': 'calc(100vh - 260px)', 'overflowY': 'auto' }}
						ref={tableDragScroll.ref}
						onMouseDown={tableDragScroll.onMouseDown}
						onMouseMove={tableDragScroll.onMouseMove}
						onMouseUp={tableDragScroll.onMouseUp}
						onMouseLeave={tableDragScroll.onMouseLeave}
						onClickCapture={tableDragScroll.onClickCapture}>
						<table className="table table-bordered table-hover table-sticky-head table-sticky-name mb-0">
							<thead>
								<tr>
									<th className="text-center align-middle">#</th>
									{sortableTh('first_name', t('waiter'), { 'minWidth': '160px' }, true)}
									{sortableTh('working_days', t('working_days'))}
									{sortableTh('count_cheque', t('cheques'))}
									{sortableTh('people_number', t('guests'))}
									{sortableTh('count_products', t('products'))}
									{sortableTh('average_cheque', t('average_cheque'))}
									{sortableTh('sales_per_day', t('sales_per_day'))}
									{sortableTh('total_service_amount', t('service'))}
									{sortableTh('sales', t('sales_amount'))}
									{/* Доля продаж — та же сумма продаж, сортируется по ней */}
									<th style={{ 'width': '1%' }} className="text-end th-sortable user-select-none align-middle"
										onClick={() => sortTable('sales')} title={t('sort_by')}>
										<div className="d-flex align-items-center justify-content-end gap-1">
											<span>{t('share_of_sales')}</span>
											{renderSortIndicator('sales', sortState)}
										</div>
									</th>
									{sortableTh('gross', t('gross_income'))}
									{sortableTh('motivation_rates', t('motivation_rate'))}
									{sortableTh('motivation_bonus', t('motivation_accrued'))}
								</tr>
							</thead>
							<tbody>
								{sortedRows.map((item, index) => (
									<tr className="cursor" key={index} onClick={() => toggleModalDetail(item)}>
										<td className="text-center">{index + 1}</td>
										<td className="td-ellipsis"
											onClick={() => copyText(item.waiter_login)}
											title={item.waiter_login}>
											{item.first_name}
										</td>
										<td className="text-end text-nowrap">{item.working_days}</td>
										<td className="text-end text-nowrap">{formatMoney(item.count_cheque)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.people_number)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.count_products)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.count_cheque > 0 ? item.sales / item.count_cheque : 0)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.working_days > 0 ? item.sales / item.working_days : 0)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.total_service_amount)}</td>
										<td className="text-end text-nowrap fw-semibold">{formatMoney(item.sales)}</td>
										<td className="text-end text-nowrap">{share(item.sales)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.gross)}</td>
										<td className="text-end text-nowrap" title={ratesTitle(item.motivation_rates)}>{formatRates(item.motivation_rates)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.motivation_bonus)}</td>
									</tr>
								))}
								{data?.data?.length === 0 &&
									<tr>
										<td colSpan="14" className="text-center text-muted py-4">{t('no_data')}</td>
									</tr>
								}
							</tbody>
							{data?.data?.length > 0 &&
								<tfoot>
									<tr className="fw-bold">
										<td></td>
										<td>{t('total')}</td>
										<td></td>
										<td className="text-end text-nowrap">{formatMoney(data.overall.overall_cheques)}</td>
										<td className="text-end text-nowrap">{formatMoney(data.overall.overall_people)}</td>
										<td className="text-end text-nowrap">{formatMoney(data.overall.overall_products)}</td>
										<td className="text-end text-nowrap">{formatMoney(data.overall.overall_cheques > 0 ? data.overall.overall_sales / data.overall.overall_cheques : 0)}</td>
										<td></td>
										<td className="text-end text-nowrap">{formatMoney(data.overall.overall_service_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(data.overall.overall_sales)}</td>
										<td className="text-end text-nowrap">100%</td>
										<td className="text-end text-nowrap">{formatMoney(data.overall.overall_gross)}</td>
										<td></td>
										<td className="text-end text-nowrap">{formatMoney(data.overall.overall_motivation_bonus)}</td>
									</tr>
								</tfoot>
							}
						</table>
					</div>
				</div>
			</div>

			{/* MODAL EXCLUDED */}
			<Modal show={modalExcluded} animation={false} centered backdrop="static"
				onHide={() => setModalExcluded(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('excluded_products')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="small text-muted">{t('excluded_products_hint')}</p>
					<Select
						isMulti
						options={products}
						value={selectedExcluded}
						onChange={(options) => setSelectedExcluded(options || [])}
						placeholder=""
						noOptionsMessage={() => t('list_empty')}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
					/>
					<div className="d-flex justify-content-end gap-2 mt-3">
						<button className="btn btn-outline-secondary" onClick={() => setModalExcluded(false)}>{t('cancel')}</button>
						<button className="btn btn-primary" onClick={() => saveExcluded()}>{t('save')}</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL EXCLUDED */}

			<Modal show={modalDetail} animation={false} size="lg" centered
				onHide={() => setModalDetail(false)}>
				<Modal.Body>
					<h3 className="mb-3">{selectedItem.first_name}</h3>
					<div className="table-responsive table-fixed inventory-modal-height">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'width': '33px', 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'minWidth': '40%' }} className="text-nowrap">{t('name2')}</th>
									<th style={{ 'minWidth': '10%' }} className="text-end text-nowrap">{t('price')}</th>
									<th style={{ 'minWidth': '10%' }} className="text-end text-nowrap">{t('sale_price')}</th>
									<th style={{ 'minWidth': '10%' }} className="text-end text-nowrap">{t('quantity')}</th>
									<th style={{ 'minWidth': '10%' }} className="text-end text-nowrap">{t('sales_amount')}</th>
									<th style={{ 'minWidth': '10%' }} className="text-end text-nowrap">{t('income')}</th>
								</tr>
							</thead>
							<tbody>
								{selectedItem?.products?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td>{item.name}</td>
										<td className="text-end text-nowrap">{formatMoney(item.price)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.sale_price)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.total_quantity)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.total_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.total_income)}</td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(selectedItem?.overall?.overall_price)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(selectedItem?.overall?.overall_sale_price)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(selectedItem?.overall?.overall_quantity)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(selectedItem?.overall?.overall_sales)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(selectedItem?.overall?.overall_gross)}</b></td>
								</tr>
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Index
